'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'

export type BrandingConfig = {
  companyName: string
  companyLogoUrl: string
  companyFaviconUrl: string
  primaryColor: string
  secondaryColor: string
  accentColor: string
  supportEmail: string
  supportPhone?: string
  companyWebsite?: string
  termsUrl?: string
  privacyUrl?: string
  insuranceProviderLabel: string
}

type BrandingContextType = {
  branding: BrandingConfig
  loading: boolean
  refreshBranding: () => Promise<void>
}

const defaultBranding: BrandingConfig = {
  companyName: 'PurpleTree HR',
  companyLogoUrl: '/purpletree-logo.png',
  companyFaviconUrl: '/logomark.png',
  primaryColor: '#7C3AED',
  secondaryColor: '#6D28D9',
  accentColor: '#8B5CF6',
  supportEmail: 'support@purpletree.ie',
  insuranceProviderLabel: 'PurpleTree Insurance'
}

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  loading: true,
  refreshBranding: async () => {}
})

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(defaultBranding)
  const [loading, setLoading] = useState(true)

  const fetchBranding = async () => {
    try {
      const response = await fetch('/api/settings/config?t=' + Date.now())
      if (response.ok) {
        const data = await response.json()
        if (data.config) {
          // Add cache-busting timestamp to image URLs
          const addCacheBuster = (url: string) => {
            if (!url || url.startsWith('/purpletree') || url.startsWith('/logomark')) {
              // Don't add cache buster to default assets
              return url
            }
            const separator = url.includes('?') ? '&' : '?'
            return `${url}${separator}t=${Date.now()}`
          }
          
          setBranding({
            companyName: data.config.company_name || defaultBranding.companyName,
            companyLogoUrl: addCacheBuster(data.config.company_logo_url) || defaultBranding.companyLogoUrl,
            companyFaviconUrl: addCacheBuster(data.config.company_favicon_url) || defaultBranding.companyFaviconUrl,
            primaryColor: data.config.primary_color || defaultBranding.primaryColor,
            secondaryColor: data.config.secondary_color || defaultBranding.secondaryColor,
            accentColor: data.config.accent_color || defaultBranding.accentColor,
            supportEmail: data.config.support_email || defaultBranding.supportEmail,
            supportPhone: data.config.support_phone,
            companyWebsite: data.config.company_website,
            termsUrl: data.config.terms_url,
            privacyUrl: data.config.privacy_url,
            insuranceProviderLabel: data.config.insurance_provider_label || defaultBranding.insuranceProviderLabel
          })
          
          // Inject CSS variables
          injectCSSVariables({
            primaryColor: data.config.primary_color || defaultBranding.primaryColor,
            secondaryColor: data.config.secondary_color || defaultBranding.secondaryColor,
            accentColor: data.config.accent_color || defaultBranding.accentColor
          })
        }
      }
    } catch (error) {
      // Failed to fetch branding config
    } finally {
      setLoading(false)
    }
  }

  const injectCSSVariables = ({ primaryColor, secondaryColor, accentColor }: Pick<BrandingConfig, 'primaryColor' | 'secondaryColor' | 'accentColor'>) => {
    const root = document.documentElement
    root.style.setProperty('--brand-primary', primaryColor)
    root.style.setProperty('--brand-secondary', secondaryColor)
    root.style.setProperty('--brand-accent', accentColor)
    
    // Also set theme colors for DaisyUI
    root.style.setProperty('--p', hexToHSL(primaryColor))
    root.style.setProperty('--s', hexToHSL(secondaryColor))
    root.style.setProperty('--a', hexToHSL(accentColor))
  }

  const hexToHSL = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '')
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255
    const g = parseInt(hex.substring(2, 4), 16) / 255
    const b = parseInt(hex.substring(4, 6), 16) / 255
    
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0, s = 0, l = (max + min) / 2
    
    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
        case g: h = ((b - r) / d + 2) / 6; break
        case b: h = ((r - g) / d + 4) / 6; break
      }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
  }

  useEffect(() => {
    fetchBranding()
  }, [])

  const refreshBranding = async () => {
    await fetchBranding()
  }

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  )
}

export const useBranding = () => {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}